// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { createElement, Component, findDOMNode, isRtl } from '../../utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Measure from 'react-measure';
import { CLS_PREFIX } from '../../constants';
import Icon from '../Icon';

import './Carousel.less';

/**
 * Carousel component.
 */
class Carousel extends Component {
    constructor(props) {
        super(props);
        this.state = {
            width: null,
            current: 0,
            next: 0,
            moving: false,
        };

        this.width = 0;
        this.totalWidth = 0;
        this.slideWidths = [];
        this.speed = 500;
        this.movingCallback = null;

        this.playSpeed = 5000;
        this.playTimer = null;

        this.handlePrevClick = this.handlePrevClick.bind(this);
        this.handleNextClick = this.handleNextClick.bind(this);
        this.stop = this.stop.bind(this);
        this.play = this.play.bind(this);
        this.handleResize = this.handleResize.bind(this);
    }

    componentDidMount() {
        this.isRtl = isRtl();
        const node = findDOMNode(this);
        this.slideWidths = [].map.call(node.firstChild.childNodes, el => el.clientWidth);
        this.totalWidth = this.slideWidths.reduce((prev, curr) => prev + curr, 0);
        this.play();
    }

    componentWillUnmount() {
        this.stop();
        clearTimeout(this.movingCallback);
    }

    handlePrevClick() {
        this.stop();
        this.setCurrentSlide(this.state.current - 1);
    }

    handleNextClick() {
        this.stop();
        this.setCurrentSlide(this.state.current + 1);
    }

    play() {
        this.playTimer = setTimeout(() => {
            this.setCurrentSlide(this.state.current + 1);
            this.play();
        }, this.playSpeed);
    }

    stop() {
        if (this.playTimer) {
            clearTimeout(this.playTimer);
            this.playTimer = null;
        }
    }

    setCurrentSlide(next) {
        if (this.state.moving) {
            return;
        }

        let current = next;
        if (current < 0) {
            current = this.slideWidths.length - 1;
        }
        if (current >= this.slideWidths.length) {
            current = 0;
        }

        this.setState({
            current,
            next,
            moving: true,
        });
        this.movingCallback = setTimeout(() => {
            this.movingCallback = null;
            this.setState({
                moving: false,
            });
        }, this.speed);
    }

    handleResize(contentRect) {
        this.setState({
            width: contentRect.bounds.width,
        });
    }

    listStyle() {
        const { current, next, moving } = this.state;
        const moveTo = moving ? next : current;
        let x = -this.totalWidth + 26;

        if (moveTo < 0) {
            for (let i = moveTo; i < 0; i++) {
                x += this.slideWidths[i + this.slideWidths.length];
            }
        } else {
            for (let i = 0; i < moveTo; i++) {
                x -= this.slideWidths[i % this.slideWidths.length];
            }
        }

        if (this.isRtl) {
            x = -x;
        }

        return {
            WebkitTransform: `translateX(${x ? `${x}px` : 0})`,
            transform: `translateX(${x ? `${x}px` : 0})`,
            WebkitTransition: moving ? `-webkit-transform ${this.speed}ms ease` : null,
            transition: moving ? `transform ${this.speed}ms ease` : null,
        };
    }

    render() {
        const { width } = this.state;
        const showControls = width < this.totalWidth;

        const {
            baseClassName,
            className,
            children,
            ...props
        } = this.props;

        return (
            <Measure onResize={this.handleResize} bounds>
                {({ measureRef }) => (
                    <section ref={measureRef} className={classNames(baseClassName, className)} {...props}>
                        <div className={`${baseClassName}__slides`} style={showControls ? this.listStyle() : null}>
                            {showControls && children}
                            {children}
                            {showControls && children}
                        </div>
                        {showControls && (
                            <div className={`${baseClassName}__controls`}>
                                <button type="button" className={classNames(`${baseClassName}__control`, `${baseClassName}__control--previous`)} onClick={this.handlePrevClick}>
                                    <Icon name="chevron-left" flipHorizontal={this.isRtl} className={`${baseClassName}__control-icon`} />
                                </button>
                                <button type="button" className={classNames(`${baseClassName}__control`, `${baseClassName}__control--next`)} onClick={this.handleNextClick}>
                                    <Icon name="chevron-right" flipHorizontal={this.isRtl} className={`${baseClassName}__control-icon`} />
                                </button>
                            </div>
                        )}
                    </section>
                )}
            </Measure>
        );
    }
}

Carousel.propTypes = {
    /**
     * Slides
     */
    children: PropTypes.node,
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * @ignore
     */
    baseClassName: PropTypes.string,
};

Carousel.defaultProps = {
    children: null,
    className: null,
    baseClassName: `${CLS_PREFIX}carousel`,
};

export default Carousel;



// WEBPACK FOOTER //
// ./components/Carousel/Carousel.js